import { useCallback, useEffect, useMemo, useState } from "react";
import { CurrencyIcon } from "../../../Components/Atoms/CurrencyIcon";
import { useToast } from "../../../Hooks/toast";
import { useProgressAlert, mockProgressApi } from "../../../Hooks/useProgressAlert";
import { CurrencySelect } from "../ContaInternacional/components/CurrencySelect";
import {
  formatCrossborderAmount,
  formatTransactionRoute,
  getCurrency,
  getCustomer,
  getModalPayinQuote,
  getPaymentMethod,
  getPaymentMethodSearchText,
  getValutAccount,
  getValutAccountsForCurrency,
  getValutReceiveRails,
  MOCK_CUSTOMERS,
  MOCK_VALUT_ACCOUNTS,
  PAYIN_AMOUNT_CURRENCIES,
  PAYIN_AMOUNT_LIMITS,
  PAYIN_DESTINATION_CURRENCIES,
  PAYIN_PAYMENT_METHODS,
} from "./constants";
import { CrossborderSelect } from "./components/CrossborderSelect";
import { CrossborderAmountSection } from "./components/CrossborderAmountSection";
import { CrossborderFormSection } from "./components/CrossborderFormSection";
import { OriginPaymentMethodOption } from "./components/OriginPaymentMethodOption";
import { PaymentRailIcon } from "./components/PaymentRailIcon";
import {
  buildModalPayinSummaryRows,
  CrossborderSummary,
} from "./components/CrossborderSummary";

const PAYIN_FORM_ID = "crossborder-payin-form";

const formatPaymentMethod = (method) => <OriginPaymentMethodOption method={method} />;

const formatRail = (rail) => (
  <>
    <PaymentRailIcon railId={rail.id} className="h-4 w-4" />
    <span className="min-w-0 truncate font-semibold text-brand-dark">{rail.label}</span>
  </>
);

const formatValutAccount = (account) => (
  <>
    <CurrencyIcon asset={account.currencyId} />
    <span className="min-w-0 truncate font-semibold text-brand-dark">{account.label}</span>
  </>
);

const formatCustomer = (customer) => (
  <span className="min-w-0 truncate font-semibold text-brand-dark">{customer.label}</span>
);

const amountCurrencyOptions = PAYIN_AMOUNT_CURRENCIES.map((id) => getCurrency(id)).filter(Boolean);
const destinationCurrencyOptions = PAYIN_DESTINATION_CURRENCIES.map((id) => getCurrency(id)).filter(Boolean);

export const CrossborderPayinContent = ({
  isActive = true,
  formId = PAYIN_FORM_ID,
  showPageActions = false,
  onRequestQuote,
  onClose,
}) => {
  const toast = useToast();
  const progress = useProgressAlert();
  const [paymentMethodId, setPaymentMethodId] = useState(PAYIN_PAYMENT_METHODS[0].id);
  const [destinationCurrencyId, setDestinationCurrencyId] = useState("usd");
  const [destinationRailId, setDestinationRailId] = useState("wire");
  const [customerId, setCustomerId] = useState(MOCK_CUSTOMERS[0].id);
  const [valutAccountId, setValutAccountId] = useState(MOCK_VALUT_ACCOUNTS[0].id);
  const [amountCurrencyId, setAmountCurrencyId] = useState("usd");
  const [amount, setAmount] = useState("");

  const destinationRails = useMemo(
    () => getValutReceiveRails(destinationCurrencyId),
    [destinationCurrencyId]
  );

  const valutAccounts = useMemo(
    () => getValutAccountsForCurrency(destinationCurrencyId),
    [destinationCurrencyId]
  );

  useEffect(() => {
    if (!destinationRails.some((rail) => rail.id === destinationRailId)) {
      setDestinationRailId(destinationRails[0]?.id ?? "wire");
    }
  }, [destinationRails, destinationRailId]);

  useEffect(() => {
    if (!valutAccounts.some((account) => account.id === valutAccountId)) {
      setValutAccountId(valutAccounts[0]?.id ?? "");
    }
  }, [valutAccounts, valutAccountId]);

  useEffect(() => {
    if (!isActive) {
      setPaymentMethodId(PAYIN_PAYMENT_METHODS[0].id);
      setDestinationCurrencyId("usd");
      setDestinationRailId("wire");
      setCustomerId(MOCK_CUSTOMERS[0].id);
      setValutAccountId(MOCK_VALUT_ACCOUNTS[0].id);
      setAmountCurrencyId("usd");
      setAmount("");
    }
  }, [isActive]);

  const numericAmount = parseFloat(amount.replace(",", ".")) || 0;
  const limits = PAYIN_AMOUNT_LIMITS[amountCurrencyId] ?? PAYIN_AMOUNT_LIMITS.usd;

  const quote = useMemo(
    () =>
      getModalPayinQuote({
        amountCurrencyId,
        destinationCurrencyId,
        paymentMethodId,
        destinationRailId,
        amount: numericAmount,
      }),
    [amountCurrencyId, destinationCurrencyId, paymentMethodId, destinationRailId, numericAmount]
  );

  const { rows, footerRows } = buildModalPayinSummaryRows(quote);

  const routePreview = formatTransactionRoute({
    originRailId: quote.paymentMethod?.railId,
    destinationRailId,
    fromCurrency: amountCurrencyId,
    toCurrency: destinationCurrencyId,
  });

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();

      if (
        !getPaymentMethod(paymentMethodId) ||
        !getCurrency(destinationCurrencyId) ||
        !getCustomer(customerId) ||
        !getValutAccount(valutAccountId)
      ) {
        toast.addToast({
          type: "error",
          title: "Campos obrigatórios",
          description: "Preencha todos os campos obrigatórios.",
        });
        return;
      }

      if (!numericAmount || numericAmount <= 0) {
        toast.addToast({
          type: "error",
          title: "Valor inválido",
          description: "Informe um valor maior que zero.",
        });
        return;
      }

      if (numericAmount < limits.min || numericAmount > limits.max) {
        toast.addToast({
          type: "error",
          title: "Valor fora dos limites",
          description: `O valor deve estar entre ${formatCrossborderAmount(limits.min, amountCurrencyId)} e ${formatCrossborderAmount(limits.max, amountCurrencyId)}.`,
        });
        return;
      }

      progress
        .runWithProgress(
          () => mockProgressApi(),
          {
            loading: "Solicitando cotação...",
            loadingDescription: routePreview,
            success: "Cotação solicitada",
            successDescription: `Payin ${routePreview}. Integração com API em desenvolvimento.`,
            error: "Falha ao solicitar cotação",
            errorDescription: "Não foi possível obter a cotação. Tente novamente.",
          },
          { beforeProgress: onClose }
        )
        .then(() => {
          onRequestQuote?.(quote);
        })
        .catch(() => {});
    },
    [
      amountCurrencyId,
      customerId,
      limits.max,
      limits.min,
      numericAmount,
      onClose,
      onRequestQuote,
      paymentMethodId,
      progress,
      quote,
      routePreview,
      toast,
      valutAccountId,
      destinationCurrencyId,
    ]
  );

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-4">
      <CrossborderFormSection title="Pagamento na origem">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CrossborderSelect
            id="payin-payment-method"
            label="Forma de pagamento na origem"
            required
            value={paymentMethodId}
            onChange={setPaymentMethodId}
            options={PAYIN_PAYMENT_METHODS}
            placeholder="Selecione a forma de pagamento"
            formatOption={formatPaymentMethod}
            getSelected={getPaymentMethod}
            getOptionSearchText={getPaymentMethodSearchText}
            searchPlaceholder="Buscar país, moeda ou forma de pagamento..."
          />
          <CurrencySelect
            id="payin-destination-currency"
            label="Moeda de destino"
            value={destinationCurrencyId}
            onChange={setDestinationCurrencyId}
            options={destinationCurrencyOptions}
          />
        </div>
      </CrossborderFormSection>

      <CrossborderFormSection title="Recebimento na Valut">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CrossborderSelect
            id="payin-destination-rail"
            label="Forma de recebimento na Valut"
            required
            value={destinationRailId}
            onChange={setDestinationRailId}
            options={destinationRails}
            placeholder="Selecione a forma de recebimento"
            formatOption={formatRail}
            getSelected={(id) => destinationRails.find((rail) => rail.id === id)}
          />
          <CrossborderSelect
            id="payin-customer"
            label="Cliente"
            required
            value={customerId}
            onChange={setCustomerId}
            options={MOCK_CUSTOMERS}
            placeholder="Selecione o cliente"
            formatOption={formatCustomer}
            getSelected={getCustomer}
            searchPlaceholder="Buscar cliente..."
          />
          <div className="sm:col-span-2">
            <CrossborderSelect
              id="payin-valut-account"
              label="Conta Valut de destino"
              required
              value={valutAccountId}
              onChange={setValutAccountId}
              options={valutAccounts}
              placeholder="Selecione a conta Valut"
              formatOption={formatValutAccount}
              getSelected={getValutAccount}
              emptyMessage="Nenhuma conta Valut disponível para esta moeda."
            />
          </div>
        </div>
      </CrossborderFormSection>

      <div className={numericAmount > 0 ? "grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start" : ""}>
        <CrossborderAmountSection
          title="Valor na origem"
          amountInputId="payin-amount"
          amount={amount}
          onAmountChange={setAmount}
          amountCurrencyId={amountCurrencyId}
          amountCurrencyOptions={amountCurrencyOptions}
          onAmountCurrencyChange={setAmountCurrencyId}
          showCurrencySelect
          limits={limits}
          originRailId={quote.paymentMethod?.railId}
          destinationRailId={destinationRailId}
          fromCurrency={amountCurrencyId}
          toCurrency={destinationCurrencyId}
          routePreview={routePreview}
        />

        {numericAmount > 0 && (
          <CrossborderSummary
            title="Prévia da cotação"
            subtitle="Estimativa com câmbio e taxas. Valores sujeitos a confirmação."
            rows={rows}
            footerRows={footerRows}
          />
        )}
      </div>

      {showPageActions && (
        <div className="flex flex-col gap-2 border-t border-gray-100 pt-4">
          <button type="submit" className="btn-primary w-full">
            Solicitar cotação
          </button>
        </div>
      )}
    </form>
  );
};

export const CROSSBORDER_PAYIN_FORM_ID = PAYIN_FORM_ID;
